import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Buildings, Pencil, Plus, Trash, Users, X } from "@phosphor-icons/react";
import AdminLayout from "../components/AdminLayout";
import { api, extractError } from "../lib/api";

/**
 * Iter 19.68.17 — Unternehmen (Companies) verwalten inkl. Monats-Kontingent.
 *
 * Superadmin-only Seite. Bietet:
 *   - Liste aller Companies mit user_count + usage_this_month
 *   - Anlegen / Bearbeiten / Löschen
 *   - Löschen ist hart geblockt wenn user_count > 0 (Backend-Enforcement)
 *   - Detail-Modal zeigt zugeordnete User
 */
export default function AdminCompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // "create" | "edit" | "detail" | null
  const [selected, setSelected] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/companies");
        if (!cancelled) setCompanies(data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error(extractError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  return (
    <AdminLayout
      title="Unternehmen"
      subtitle="Übergeordnete Firmen inkl. Monatskontingent"
      actions={
        <button
          type="button"
          onClick={() => { setSelected(null); setModalMode("create"); }}
          data-testid="companies-add-btn"
          className="inline-flex items-center gap-1.5 rounded-sm border border-brand-500 bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition-crisp hover:bg-brand-600"
        >
          <Plus size={14} weight="bold" />
          Neues Unternehmen
        </button>
      }
    >
      <div className="rounded-sm border border-ink-200 bg-white overflow-hidden" data-testid="companies-list">
        <table className="w-full text-sm">
          <thead className="bg-warm-50 text-[11px] uppercase tracking-widest font-semibold text-warm-500">
            <tr>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Kontingent</th>
              <th className="text-left px-4 py-2.5">Verbrauch (aktueller Monat)</th>
              <th className="text-left px-4 py-2.5">User</th>
              <th className="text-right px-4 py-2.5">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-warm-500">Wird geladen …</td></tr>
            )}
            {!loading && companies.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center">
                  <Buildings size={40} weight="thin" className="mx-auto text-warm-400" />
                  <div className="mt-3 text-sm text-warm-600">
                    Noch keine Unternehmen angelegt. Legen Sie das erste an.
                  </div>
                </td>
              </tr>
            )}
            {!loading && companies.map((c) => {
              const quotaLabel = c.monthly_quota == null
                ? "unlimitiert"
                : c.monthly_quota === 0
                  ? "pausiert"
                  : `${c.monthly_quota} / Monat`;
              const usageColor = c.monthly_quota && c.usage_this_month >= c.monthly_quota
                ? "text-red-600 font-semibold"
                : c.monthly_quota && c.usage_this_month >= c.monthly_quota * 0.8
                  ? "text-orange-600 font-medium"
                  : "text-ink-700";
              return (
                <tr key={c.id} className="border-t border-ink-100 hover:bg-warm-50" data-testid={`company-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900">{c.name}</div>
                    {c.slug && <div className="text-xs text-warm-500 mt-0.5">{c.slug}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-ink-700">{quotaLabel}</td>
                  <td className={`px-4 py-3 text-sm ${usageColor}`}>
                    {c.usage_this_month}
                    {c.monthly_quota != null && c.monthly_quota > 0 && ` / ${c.monthly_quota}`}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <button
                      type="button"
                      onClick={() => { setSelected(c); setModalMode("detail"); }}
                      className="inline-flex items-center gap-1 text-ink-700 hover:text-brand-600 hover:underline"
                      data-testid={`company-users-${c.id}`}
                    >
                      <Users size={14} />
                      {c.user_count}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        type="button"
                        onClick={() => { setSelected(c); setModalMode("edit"); }}
                        data-testid={`company-edit-${c.id}`}
                        className="p-1.5 rounded-sm text-ink-500 hover:text-brand-600 hover:bg-warm-100 transition-crisp"
                        title="Bearbeiten"
                      >
                        <Pencil size={14} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!window.confirm(`Unternehmen „${c.name}" wirklich löschen?`)) return;
                          try {
                            await api.delete(`/admin/companies/${c.id}`);
                            toast.success("Unternehmen gelöscht.");
                            refresh();
                          } catch (e) {
                            toast.error(extractError(e));
                          }
                        }}
                        data-testid={`company-delete-${c.id}`}
                        className="p-1.5 rounded-sm text-ink-500 hover:text-red-600 hover:bg-red-50 transition-crisp disabled:opacity-40 disabled:cursor-not-allowed"
                        title={c.user_count > 0 ? `Erst ${c.user_count} User umziehen` : "Löschen"}
                        disabled={c.user_count > 0}
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalMode === "create" && (
        <CompanyFormModal
          mode="create"
          onClose={() => setModalMode(null)}
          onSaved={() => { setModalMode(null); refresh(); }}
        />
      )}
      {modalMode === "edit" && selected && (
        <CompanyFormModal
          mode="edit"
          company={selected}
          onClose={() => setModalMode(null)}
          onSaved={() => { setModalMode(null); refresh(); }}
        />
      )}
      {modalMode === "detail" && selected && (
        <CompanyDetailModal
          company={selected}
          onClose={() => setModalMode(null)}
        />
      )}
    </AdminLayout>
  );
}

function CompanyFormModal({ mode, company, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(company?.name || "");
  const [slug, setSlug] = useState(company?.slug || "");
  const [quotaMode, setQuotaMode] = useState(
    company?.monthly_quota == null ? "unlimited" : "limit",
  );
  const [monthlyQuota, setMonthlyQuota] = useState(
    company?.monthly_quota != null ? String(company.monthly_quota) : "10",
  );
  const [billingEmail, setBillingEmail] = useState(company?.billing_email || "");
  const [notes, setNotes] = useState(company?.notes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name ist Pflicht."); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim() || null,
        billing_email: billingEmail.trim() || null,
        notes: notes.trim(),
      };
      if (quotaMode === "unlimited") {
        payload.unlimited_quota = true;
      } else {
        const n = parseInt(monthlyQuota, 10);
        if (!Number.isFinite(n) || n < 0) {
          toast.error("Kontingent muss ≥ 0 sein.");
          setSaving(false);
          return;
        }
        payload.monthly_quota = n;
      }
      if (isEdit) {
        await api.patch(`/admin/companies/${company.id}`, payload);
        toast.success("Unternehmen aktualisiert.");
      } else {
        // Bei Create: unlimited_quota-Feld existiert im CompanyCreate-Schema
        // nicht — Backend interpretiert null als unlimited. Also klassisch.
        const createPayload = { ...payload };
        delete createPayload.unlimited_quota;
        if (quotaMode === "unlimited") {
          createPayload.monthly_quota = null;
        }
        await api.post("/admin/companies", createPayload);
        toast.success("Unternehmen angelegt.");
      }
      onSaved();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={isEdit ? "Unternehmen bearbeiten" : "Neues Unternehmen"} onClose={onClose} testId="company-form-modal">
      <div className="space-y-4">
        <Field label="Name *">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="company-form-name"
            className="w-full px-3 py-2 border border-ink-200 rounded-sm focus:outline-none focus:border-brand-400"
            maxLength={160}
          />
        </Field>
        <Field label="Slug (optional, für URLs)">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            data-testid="company-form-slug"
            className="w-full px-3 py-2 border border-ink-200 rounded-sm focus:outline-none focus:border-brand-400"
            maxLength={60}
            placeholder="z.B. muster-gmbh"
          />
        </Field>
        <Field label="Monatliches Kontingent">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="quotaMode"
                value="unlimited"
                checked={quotaMode === "unlimited"}
                onChange={() => setQuotaMode("unlimited")}
                data-testid="company-form-quota-unlimited"
              />
              Unlimitiert
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="quotaMode"
                value="limit"
                checked={quotaMode === "limit"}
                onChange={() => setQuotaMode("limit")}
                data-testid="company-form-quota-limit"
              />
              Limit setzen:
              <input
                type="number"
                min={0}
                value={monthlyQuota}
                onChange={(e) => setMonthlyQuota(e.target.value)}
                disabled={quotaMode !== "limit"}
                data-testid="company-form-quota-value"
                className="w-24 px-2 py-1 border border-ink-200 rounded-sm text-sm disabled:bg-warm-50 disabled:text-warm-400"
              />
              <span className="text-xs text-warm-500">Einstufungen / Kalendermonat (0 = pausiert)</span>
            </label>
          </div>
        </Field>
        <Field label="Rechnungs-E-Mail (optional)">
          <input
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            data-testid="company-form-billing-email"
            className="w-full px-3 py-2 border border-ink-200 rounded-sm focus:outline-none focus:border-brand-400"
          />
        </Field>
        <Field label="Interne Notizen (nur Admin)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="company-form-notes"
            className="w-full px-3 py-2 border border-ink-200 rounded-sm focus:outline-none focus:border-brand-400 text-sm"
          />
        </Field>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm text-ink-700 hover:bg-warm-100 rounded-sm"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="company-form-save"
          className="px-4 py-2 text-sm bg-brand-500 text-white rounded-sm hover:bg-brand-600 disabled:opacity-50"
        >
          {saving ? "Speichere …" : (isEdit ? "Speichern" : "Anlegen")}
        </button>
      </div>
    </ModalShell>
  );
}

function CompanyDetailModal({ company, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/companies/${company.id}`);
        setDetail(data);
      } catch (e) {
        toast.error(extractError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [company.id]);

  return (
    <ModalShell title={`Zugeordnete User — ${company.name}`} onClose={onClose} testId="company-detail-modal">
      {loading && <div className="py-8 text-center text-warm-500">Lädt …</div>}
      {!loading && detail && (
        <div>
          {detail.users.length === 0 ? (
            <div className="py-8 text-center text-warm-500">
              Keine User zugeordnet. Öffnen Sie die Benutzerverwaltung und weisen Sie welche zu.
            </div>
          ) : (
            <div className="border border-ink-200 rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-warm-50 text-[11px] uppercase tracking-widest font-semibold text-warm-500">
                  <tr>
                    <th className="text-left px-3 py-2">Name</th>
                    <th className="text-left px-3 py-2">E-Mail</th>
                    <th className="text-left px-3 py-2">Rolle</th>
                    <th className="text-left px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.users.map((u) => (
                    <tr key={u.id} className="border-t border-ink-100">
                      <td className="px-3 py-2">{u.name || "—"}</td>
                      <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                      <td className="px-3 py-2">{u.role}</td>
                      <td className="px-3 py-2">{u.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm bg-ink-100 text-ink-700 rounded-sm hover:bg-ink-200"
        >
          Schließen
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onClose, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={testId}
    >
      <div
        className="bg-white rounded-sm border border-ink-200 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200">
          <h2 className="text-lg font-cabinet font-medium text-ink-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-sm text-ink-500 hover:text-ink-900 hover:bg-warm-100"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
